import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftRight, Briefcase, ShoppingBag, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RoleSwitcher = ({ compact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    user, 
    activeRole, 
    setActiveRole, 
    hasRole, 
    canSwitchRoles, 
    isRoleSwitchBlocked,
    enableRoleSwitch 
  } = useAuth();

  // Fix B: refresh /auth/me on demand so a legacy vendor whose `roles[]` is
  // missing 'customer' can self-heal without logging out. Used by the
  // defensive fallback card below.
  const [refreshingRoles, setRefreshingRoles] = useState(false);
  const handleRefreshRoles = async () => {
    if (refreshingRoles) return;
    setRefreshingRoles(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token');
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      localStorage.setItem('user', JSON.stringify(res.data));
      toast.success('Account refreshed. Reloading...');
      // Hard reload to re-run AuthProvider hydration with the new user object.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error('Could not refresh. Please sign out and back in.');
      setRefreshingRoles(false);
    }
  };

  // Don't render if feature is disabled
  if (!enableRoleSwitch) {
    return null;
  }

  const isBlocked = isRoleSwitchBlocked(location.pathname);
  const canSwitch = canSwitchRoles();
  const isVendorMode = activeRole === 'vendor';
  const hasVendorRole = hasRole('vendor');

  const handleSwitch = () => {
    if (isBlocked) return;
    
    const newRole = isVendorMode ? 'customer' : 'vendor';
    setActiveRole(newRole);
    
    // Navigate to appropriate dashboard (without any leftover URL params)
    if (newRole === 'vendor') {
      navigate('/vendor/dashboard', { replace: true });
    } else {
      // Clear any tab params to avoid conflicts - start fresh on customer dashboard
      navigate('/customer-dashboard?tab=welcome', { replace: true });
    }
  };

  const handleBecomeProvider = () => {
    navigate('/become-provider');
  };

  // Compact mode for Account page - simpler card design
  if (compact) {
    if (canSwitch) {
      return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isVendorMode ? 'bg-emerald-100' : 'bg-blue-100'}`}>
              {isVendorMode ? (
                <Briefcase className="h-5 w-5 text-emerald-600" />
              ) : (
                <ShoppingBag className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {isVendorMode ? 'Provider Mode' : 'Customer Mode'}
              </p>
              <p className="text-xs text-gray-500">
                {isVendorMode ? 'Managing your business' : 'Browsing services'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSwitch}
            disabled={isBlocked}
            size="sm"
            variant="outline"
          >
            <ArrowLeftRight className="h-4 w-4 mr-1" />
            Switch
          </Button>
        </div>
      );
    }
    
    // Customer without vendor role - compact "Become Provider"
    if (!hasVendorRole && activeRole === 'customer') {
      return (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <ShoppingBag className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Customer Mode</p>
              <p className="text-xs text-gray-500">Browsing services</p>
            </div>
          </div>
          <Button
            onClick={handleBecomeProvider}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Briefcase className="h-4 w-4 mr-1" />
            Become Provider
          </Button>
        </div>
      );
    }

    // Fix B: Defensive fallback for legacy single-role vendor accounts whose
    // `roles[]` is out of sync (missing 'customer'). Instead of an empty
    // MODE card with no escape, surface a brief "Syncing..." card with a
    // Refresh button that re-fetches /auth/me and reloads.
    // Conditions: account is a vendor in some sense, but can't dual-switch.
    if (hasVendorRole && !canSwitch) {
      return (
        <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="role-sync-fallback">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-amber-100 shrink-0">
              <AlertCircle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-gray-900">Syncing account roles</p>
              <p className="text-xs text-gray-500 truncate">Tap refresh to re-sync your access</p>
            </div>
          </div>
          <Button
            onClick={handleRefreshRoles}
            disabled={refreshingRoles}
            size="sm"
            variant="outline"
            data-testid="role-sync-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshingRoles ? 'animate-spin' : ''}`} />
            {refreshingRoles ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      );
    }

    // No-null fallback: instead of silently rendering nothing (which leaves
    // an empty MODE card on TestFlight), always surface a visible card with
    // a refresh affordance so the user has an escape hatch.
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="role-unavailable-fallback">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-gray-100 shrink-0">
            <AlertCircle className="h-5 w-5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900">Mode unavailable</p>
            <p className="text-xs text-gray-500 truncate">Tap refresh to re-sync your account</p>
          </div>
        </div>
        <Button
          onClick={handleRefreshRoles}
          disabled={refreshingRoles}
          size="sm"
          variant="outline"
          data-testid="role-unavailable-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshingRoles ? 'animate-spin' : ''}`} />
          {refreshingRoles ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
    );
  }

  // Full mode (original design)
  // User can switch between roles (has both roles)
  if (canSwitch) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-emerald-600" />
            Switch Mode
          </CardTitle>
          <CardDescription>
            You're currently in {isVendorMode ? 'Provider' : 'Customer'} mode
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isBlocked ? (
            <Alert variant="warning" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please finish this step before switching modes.
              </AlertDescription>
            </Alert>
          ) : null}
          
          <Button
            onClick={handleSwitch}
            disabled={isBlocked}
            className="w-full"
            variant={isVendorMode ? 'outline' : 'default'}
          >
            {isVendorMode ? (
              <>
                <ShoppingBag className="h-4 w-4 mr-2" />
                Switch to Customer Mode
              </>
            ) : (
              <>
                <Briefcase className="h-4 w-4 mr-2" />
                Switch to Provider Mode
              </>
            )}
          </Button>
          
          <p className="text-xs text-gray-500 mt-2 text-center">
            {isVendorMode 
              ? 'Browse and book services as a customer'
              : 'Manage your listings and bookings as a provider'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // User is a customer without vendor role - show "Become a Provider" option
  if (!hasVendorRole && activeRole === 'customer') {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-blue-600" />
            Become a Provider
          </CardTitle>
          <CardDescription>
            Start offering your services on Celey
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleBecomeProvider}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            Switch to Providing
          </Button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Set up your provider profile and start earning
          </p>
        </CardContent>
      </Card>
    );
  }

  // Vendor without customer role (shouldn't happen after migration, but handle gracefully)
  if (hasVendorRole && !hasRole('customer')) {
    return (
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gray-600" />
            Provider Account
          </CardTitle>
          <CardDescription>
            You're signed in as a service provider
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Contact support if you'd like to also browse and book services as a customer.
          </p>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default RoleSwitcher;
